import { AdditionalProperites, UserSettings } from "@/lib/decks-utils/utils";
import { CardProps } from "./study-session";
import { Folder } from "@/database/db";

export type UserSync = {
  addedAiDecks: string[];
  decks: Record<string, CardProps[]>;
  additionalProperites: Record<string, AdditionalProperites>;
  folders: Folder[];
  settings: UserSettings;
};

import { useEffect, useMemo } from "react";
import debounce from "lodash/debounce";
import { compressData, shouldCompress } from "@/lib/compression/utils";
import { clientRPC } from "@/utils/hono-client";
import { useMutation } from "@tanstack/react-query";

let USE_COMPRESSION = false;
export const useSyncMutation = () => {
  return useMutation({
    mutationFn: async ({
      data,
      latestLocalUpdate,
    }: {
      data: UserSync;
      latestLocalUpdate: string;
    }) => {
      USE_COMPRESSION = USE_COMPRESSION === false ? shouldCompress(data) : true;
      console.log(
        "%cMyProject%cline:106%cUSE_COMPRESSION",
        "color:#fff;background:#ee6f57;padding:3px;border-radius:2px",
        "color:#fff;background:#1f3c88;padding:3px;border-radius:2px",
        "color:#fff;background:rgb(3, 38, 58);padding:3px;border-radius:2px",
        USE_COMPRESSION
      );

      return clientRPC.api["user-status"]["sync-user"].$post({
        json: {
          compressed: USE_COMPRESSION,
          data: USE_COMPRESSION ? compressData(data) : data,
          latestLocalUpdate,
        },
        headers: {
          "Content-Type": "application/json",
          ...(USE_COMPRESSION ? { "Content-Encoding": "gzip" } : {}),
        },
      });
    },
    onError: (error) => {
      console.error("Error syncing user:", error);
    },
  });
};

const DEBOUNCE_DELAY = 10_000;
const MAX_WAIT = 30_000;

export const debouncedUserSync = debounce(
  (payload: Payload, syncUser) => {
    prepareAndSyncUserData({ payload: payload, syncUser: syncUser });
  },
  DEBOUNCE_DELAY,
  { maxWait: MAX_WAIT, leading: true, trailing: true }
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useDebounceSync2 = () => {
  const { mutate: syncUser } = useSyncMutation();

  const debouncedSync = useMemo(
    () =>
      debounce(
        (payload) =>
          prepareAndSyncUserData({ payload: payload, syncUser: syncUser }),

        DEBOUNCE_DELAY,
        { maxWait: MAX_WAIT, leading: true, trailing: true }
      ),
    []
  );

  // Cancel pending sync on unmount
  useEffect(() => {
    return () => debouncedSync.cancel();
  }, [debouncedSync]);

  return debouncedSync;
};

export type Payload = {
  localDeck: Record<string, CardProps[]>;
  additionalProperites: Record<string, AdditionalProperites>;
  folders: Folder[];
  latestLocalUpdate: string;
  settings: UserSettings;
};
export function prepareAndSyncUserData({
  payload,
  syncUser,
}: {
  payload: Payload;
  syncUser: (params: { data: UserSync; latestLocalUpdate: string }) => void;
}): void {
  const {
    localDeck,
    additionalProperites,
    folders,
    latestLocalUpdate,
    settings,
  } = payload;

  const data: UserSync = {
    addedAiDecks: localDeck ? Object.keys(localDeck) : [],
    decks: localDeck,
    additionalProperites: additionalProperites,
    folders: folders,
    settings: settings,
  };

  syncUser({
    data,
    latestLocalUpdate,
  });
}
